/**
 * Video play/pause controls
 *
 * Initializes video play/pause controls for all buttons with class 'video-control-button'
 * If button has data-controls attribute, finds video by that ID
 * Otherwise, finds the closest sibling video element
 *
 * Supports both native <video> elements and Vimeo iframe embeds. The Vimeo
 * Player API is loaded on demand and Vimeo playback state/events are async
 * (delivered via postMessage), unlike native <video>.
 */

const MEDIA_SELECTOR = 'video, iframe[src*="vimeo.com"]';
const isVimeoIframe = (el) => !!el && el.tagName === 'IFRAME' && /vimeo\.com/.test(el.src || '');

const VIMEO_API_SRC = 'https://player.vimeo.com/api/player.js';
let vimeoApiPromise = null;

function loadVimeoPlayerAPI() {
  if (vimeoApiPromise) return vimeoApiPromise;

  vimeoApiPromise = new Promise((resolve, reject) => {
    if (typeof Vimeo !== 'undefined' && Vimeo.Player) {
      resolve();
      return;
    }

    const existing = document.querySelector(`script[src="${VIMEO_API_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = VIMEO_API_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return vimeoApiPromise;
}

// Vimeo.Player instances are stateful, so reuse one per iframe rather than
// recreating it on every findAssociatedVideo() call.
const vimeoPlayers = new WeakMap();
function getVimeoPlayer(iframe) {
  if (!vimeoPlayers.has(iframe)) {
    vimeoPlayers.set(iframe, new Vimeo.Player(iframe));
  }
  return vimeoPlayers.get(iframe);
}

/**
 * Initializes video play/pause controls for all buttons with class 'video-control-button'
 * @param {string} buttonSelector - CSS selector for control buttons (default: '.video-control-button')
 * @param {string} iconSelector - CSS selector for icon elements (default: '.material-symbols-sharp')
 */
export function initVideoControls(buttonSelector = '.video-control-button', iconSelector = '.material-symbols-sharp') {
  const controlButtons = document.querySelectorAll(buttonSelector);

  controlButtons.forEach(button => {
    // Helper to find the associated video (native <video> or Vimeo iframe) for this button
    const findAssociatedVideo = () => {
      let video = null;

      // Get video by ID from data-controls attribute
      const videoId = button.getAttribute('data-controls');
      if (videoId) {
        video = document.getElementById(videoId);
      }
      // Find closest related video element
      else {
        // Check previous siblings first
        let sibling = button.previousElementSibling;
        while (sibling) {
          if (sibling.matches(MEDIA_SELECTOR)) {
            video = sibling;
            break;
          }
          sibling = sibling.previousElementSibling;
        }

        // If not found, check closest .banner parent for the active (display: block) video
        if (!video) {
          const banner = button.closest('.banner');
          if (banner) {
            const bannerVideos = banner.querySelectorAll(MEDIA_SELECTOR);
            let fallbackVideo = null;

            for (const bannerVideo of bannerVideos) {
              if (!fallbackVideo) {
                fallbackVideo = bannerVideo;
              }

              const style = window.getComputedStyle(bannerVideo);
              if (style.display === 'block') {
                video = bannerVideo;
                break;
              }
            }

            // If no video is currently display:block yet (e.g. before inline randomizer script runs),
            // fall back to the first banner video so controls remain available.
            if (!video && fallbackVideo) {
              video = fallbackVideo;
            }
          }
        }

        // If still not found, check next siblings
        if (!video) {
          sibling = button.nextElementSibling;
          while (sibling) {
            if (sibling.matches(MEDIA_SELECTOR)) {
              video = sibling;
              break;
            }
            sibling = sibling.nextElementSibling;
          }
        }
      }

      return video;
    };

    // Sync icon text to a known play state
    const syncIcon = (paused) => {
      const icon = button.querySelector(iconSelector);
      if (icon) icon.textContent = paused ? 'play_arrow' : 'pause';
    };

    // Attach play/pause listeners so icon stays in sync (e.g. if autoplay is blocked)
    const bindVideoEvents = (video) => {
      video.addEventListener('play',  () => syncIcon(false));
      video.addEventListener('pause', () => syncIcon(true));
    };

    const bindVimeoEvents = (player) => {
      player.on('play',  () => syncIcon(false));
      player.on('pause', () => syncIcon(true));
    };

    // Fully initialise the button once a video is available
    const activateButton = (video) => {
      button.style.display = '';

      if (isVimeoIframe(video)) {
        loadVimeoPlayerAPI().then(() => {
          const player = getVimeoPlayer(video);
          bindVimeoEvents(player);
          // Skip icon sync if video hasn't started yet (pre-autoplay). The HTML
          // default icon is already correct; play/pause events take over from here.
          Promise.all([player.getPaused(), player.getCurrentTime()]).then(([paused, time]) => {
            if (!paused || time > 0) syncIcon(paused);
          });
        }).catch((error) => {
          console.warn('[video-controls] Failed to load Vimeo Player API:', error);
        });
        return;
      }

      bindVideoEvents(video);
      // Skip icon sync if video hasn't started yet (currentTime===0, paused = pre-autoplay).
      // The HTML default icon is already correct; play/pause events take over from here.
      if (!video.paused || video.currentTime > 0) {
        syncIcon(video.paused);
      }
    };

    // Click handler — always uses live findAssociatedVideo() so it works even
    // when the active video changes (e.g. randomiser, Interchange swap)
    button.addEventListener('click', function() {
      const video = findAssociatedVideo();
      if (!video) return;

      if (isVimeoIframe(video)) {
        loadVimeoPlayerAPI().then(() => {
          const player = getVimeoPlayer(video);
          player.getPaused().then((paused) => {
            if (paused) {
              player.play();
              syncIcon(false);
            } else {
              player.pause();
              syncIcon(true);
            }
          });
        });
        return;
      }

      if (video.paused) {
        video.play();
        syncIcon(false);
      } else {
        video.pause();
        syncIcon(true);
      }
    });

    const initialVideo = findAssociatedVideo();
    if (initialVideo) {
      activateButton(initialVideo);
    } else {
      // No video in DOM yet — hide button but watch for one to appear
      // (covers Foundation Interchange loading video markup asynchronously)
      button.style.display = 'none';

      const bannerParent = button.closest('.banner');
      if (!bannerParent) return; // No banner context; nothing to watch

      const observer = new MutationObserver(() => {
        const video = findAssociatedVideo();
        if (video) {
          observer.disconnect();
          activateButton(video);
        }
      });
      // Watch for new child elements (Interchange injection) and style changes
      // (inline randomiser setting display:block on a pre-existing video)
      observer.observe(bannerParent, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });
    }
  });
}

/**
 * Auto-initializes video controls when DOM is ready
 */
export function autoInitVideoControls() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initVideoControls());
  } else {
    initVideoControls();
  }
}

autoInitVideoControls();

// Default export so other modules can use
export default {
  initVideoControls,
  autoInitVideoControls
};

